import React, { useState } from "react";
import { X } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function DomainFormDialog({ providers, tenants, onClose, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    tenant_id: (tenants[0] || {}).tenant_id || "",
    domain: "",
    subdomain: "",
    dns_provider: "MANUAL",
    ssl_provider: "LETS_ENCRYPT",
    target_ip: "",
    target_hostname: "",
    notes: "",
  });

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.tenant_id) { toast.error("Pilih tenant."); return; }
    if (!form.domain || !form.domain.includes(".")) { toast.error("Masukkan domain valid."); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      const r = await api.post("/platform/domains", payload);
      toast.success(`Domain ${r.data.domain.fqdn} dibuat.`);
      onSuccess?.(r.data.domain);
    } catch (e) { notifyError(e, "Gagal buat domain"); }
    finally { setBusy(false); }
  };

  const fieldCls = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-500/60";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="dom-form-dialog">
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#121D33] p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="text-white font-display font-black text-lg">Tambah Domain</div>
          <Button variant="ghost" size="sm" onClick={onClose} className="ml-auto text-white/60 hover:text-white hover:bg-white/10" data-testid="dom-form-close">
            <X size={14} />
          </Button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Tenant</label>
            <select value={form.tenant_id} onChange={(e) => setF("tenant_id", e.target.value)} className={fieldCls} data-testid="dom-form-tenant">
              <option value="">— Pilih tenant —</option>
              {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Domain</label>
              <input value={form.domain} onChange={(e) => setF("domain", e.target.value)} placeholder="example.com" className={fieldCls} data-testid="dom-form-domain" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Subdomain (opsional)</label>
              <input value={form.subdomain} onChange={(e) => setF("subdomain", e.target.value)} placeholder="www / api" className={fieldCls} data-testid="dom-form-subdomain" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">DNS Provider</label>
              <select value={form.dns_provider} onChange={(e) => setF("dns_provider", e.target.value)} className={fieldCls} data-testid="dom-form-dns-provider">
                {providers?.dns?.map(p => (
                  <option key={p.code} value={p.code}>{p.label}{!p.ready ? " (stub)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">SSL Provider</label>
              <select value={form.ssl_provider} onChange={(e) => setF("ssl_provider", e.target.value)} className={fieldCls} data-testid="dom-form-ssl-provider">
                {providers?.ssl?.map(p => (
                  <option key={p.code} value={p.code}>{p.label}{!p.ready ? " (stub)" : ""}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Target IP (A record)</label>
              <input value={form.target_ip} onChange={(e) => setF("target_ip", e.target.value)} placeholder="203.0.113.10" className={fieldCls} data-testid="dom-form-target-ip" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Target Hostname (CNAME)</label>
              <input value={form.target_hostname} onChange={(e) => setF("target_hostname", e.target.value)} placeholder="edge.dynicty.com" className={fieldCls} data-testid="dom-form-target-hostname" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} className={fieldCls} data-testid="dom-form-notes" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="dom-form-cancel">Batal</Button>
          <Button onClick={submit} disabled={busy} className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-black" data-testid="dom-form-submit">
            {busy ? "Menyimpan…" : "Simpan"}
          </Button>
        </div>
      </div>
    </div>
  );
}
